import { Box } from '@mui/material';
import { FEED_CARD_WIDTH } from "app/appConstants";
import { MprcContainer, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcFilterCountInfo } from 'components/filter';
import RequestErrorUtils from "components/MprcRequestErrorReport";
import { FeedDataDto, FeedItemDto, FeedListQueryOrderBy, FeedType, PublicationListType, PublicationSelectorDtoPaginatedList, PublicationSelectorOrder, PublicationSelectorType } from 'generated/CoreApi_v1';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { CardManager } from 'views/knowledge/components/card/CardManager';
import { CardListHeaderSkeleton } from "views/knowledge/components/card/CardSkeleton";
import { publish } from "views/knowledge/feed/FeedEvents";
import { ListPublicationSelectorDrawer, PublicationSelectorDrawerResult } from 'views/knowledge/publication/drawers/ListPublicationSelectorDrawer';
import { FeedListBox } from "views/knowledge/publication/pages/List/FeedListBox";
import { FeedListFilter, IFeedListFilterOptions } from 'views/knowledge/publication/pages/List/FeedListFilter';

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    listType: PublicationListType;
}

////////////////////////////////////////////////////////////////////////////////////////////////

export const ListSystemViewer: React.FC<IProps> = (props) => {

    const { listType } = props;

    const api = useCoreApi();
    const location = useLocation();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [publicationId, setPublicationId] = useState<string | undefined>();
    const [publication, setPublication] = useState<FeedItemDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [addItemList, setAddItemList] = useState<PublicationSelectorDrawerResult | null>(null);
    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();
    const [count, setCount] = useState<number | undefined | null>();
    const [filterOptions, setFilterOptions] = useState<IFeedListFilterOptions>(location.state?.filter ?? {
        orderBy: FeedListQueryOrderBy.DisplayOrder,
        searchText: ""
    });

    const filtered = filterOptions.searchText.length > 0;

    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    const handleFilterChange = useCallback((filter: IFeedListFilterOptions) => {
        setFilterOptions(filter);
        setCount(null);
    }, []);

    ///
    const handleRefreshData = (data?: FeedDataDto, resetFilter?: boolean) => {
        if (resetFilter) {
            setFilterOptions({
                orderBy: FeedListQueryOrderBy.MostRecent,
                searchText: ""
            });
            setCount(null);
        }
        setCount(data?.totalRows ?? 0);
    };

    ///
    const handleAdd = () => {
        setDialog(
            <ListPublicationSelectorDrawer
                title={t("Feed.Card.List.PublicationDrawerTitle")}
                onSelect={(publication) => setAddItemList(publication)}
                onClose={() => setDialog(null)}
                onAddNewPost={() => {
                    publish("ListItemAdded", publicationId!);
                    setDialog(null);
                }}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<PublicationSelectorDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeSelectors.publications({
                            searchText,
                            pageNumber,
                            pageSize,
                            selectorType: PublicationSelectorType.AddListItem,
                            listPublicationId: publicationId ?? null,
                            onlyPostPublications: false,
                            orderBy: PublicationSelectorOrder.Title,
                            userId: null
                        }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ///
    const load = () => {
        setViewError(null);

        api.v1.knowledgeFeed.getFeedListByType({ listType })
            .then((result) => {
                setPublication(result.data);
                setPublicationId(result.data.publicationId);
            })
            .catch((error: any) => {
                setViewError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        load();
    }, []);

    ///
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.knowledgePublication.addListItem({
                    listPublicationId: publicationId!,
                    itemPublicationId: addItemList!.publicationId
                });

                if (result) {
                    publish("ListItemAdded", publicationId!);
                }
            }
            catch (e) {
                const errorMessage = RequestErrorUtils.findValidationMessage(e, "itemPublicationId", "ListPublicationItemAlreadyExists");

                if (errorMessage) enqueueSnackbar(errorMessage, { variant: "error" });
                else setViewError(e);
            }
            finally {
                setAddItemList(null);
            }
        }

        if (addItemList) postData();

    }, [addItemList]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderCardHeader = useCallback(() => {
        return publication
            ? <Box sx={{ width: FEED_CARD_WIDTH, margin: "0 auto" }}>
                <MprcContainer>
                    <CardManager
                        feedType={FeedType.ListSystemViewer}
                        pub={publication}
                        onNeedResetRowHeight={() => { }}
                        onReload={() => null}
                        onFeedItemChanged={load}
                    />
                    <FeedListFilter
                        filterOptions={filterOptions}
                        listType={publication.listDefinition?.listType}
                        onFilterChange={handleFilterChange}
                        onAdd={handleAdd}
                    />
                </MprcContainer>

                {
                    count != undefined &&
                    <MprcFilterCountInfo
                        variant={filtered ? "bodyNotice" : (count ?? 0) == 0 ? "captionHeader" : "caption"}
                        text={t("Feed.List.FilterInfoMessage", { count: count ?? 0 })}
                        onResetFilter={filtered ? () => handleFilterChange({ ...filterOptions, searchText: "" }) : undefined}
                    />
                }
            </Box>
            : <Box></Box>;
    }, [publication, count]);

    ///

    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => load()}
        />
        : loading
            ? (
                <Box sx={{ width: FEED_CARD_WIDTH, margin: "0 auto" }}>
                    <CardListHeaderSkeleton visible />
                </Box>
            )
            : (
                <>
                    {dialog}

                    <MprcRequestIndicator visible={addItemList != null} />

                    <FeedListBox
                        publication={publication!}
                        feedType={FeedType.ListViewer}
                        cardHeader={RenderCardHeader()}
                        filter={filterOptions}
                        count={count}
                        permissions={{
                            canAddItems: true,
                            canCreateExclusive: false,
                            canEdit: true
                        }}
                        onRefreshData={handleRefreshData}
                        onGobackStart={() => setCount(null)}
                        onError={(error) => setViewError(error)}
                    />
                </>
            );
};
